import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';

interface Condition {
    entity: 'CODE' | 'WORKITEM';
    field: string;
    operator: string;
    value: string;
}

interface ExpressionBuilderProps {
    expression: string;
    onChange: (expression: string) => void;
    disabled?: boolean;
}

const CODE_FIELDS = [
    { value: 'original', label: 'Original', type: 'boolean' as const },
    { value: 'code', label: 'Code', type: 'string' as const },
    { value: 'sequenceNumber', label: 'Sequence Number', type: 'number' as const },
    { value: 'codeType', label: 'Code Type', type: 'string' as const },
];

const WORKITEM_FIELDS = [
    { value: 'status', label: 'Status', type: 'string' as const },
    { value: 'flow', label: 'Flow', type: 'string' as const },
    { value: 'serviceLine', label: 'Service Line', type: 'string' as const },
    { value: 'caseCategory', label: 'Case Category', type: 'string' as const },
    { value: 'insurance', label: 'Insurance', type: 'string' as const },
    { value: 'patientClass', label: 'Patient Class', type: 'string' as const },
    { value: 'facility', label: 'Facility', type: 'string' as const },
    { value: 'visitReason', label: 'Visit Reason', type: 'string' as const },
    { value: 'examDescription', label: 'Exam Description', type: 'string' as const },
    { value: 'patientGender', label: 'Patient Gender', type: 'string' as const },
    { value: 'hospitalAccountRecord', label: 'Hospital Account Record', type: 'string' as const },
    { value: 'referringProviderName', label: 'Referring Provider', type: 'string' as const },
    { value: 'performingProviderName', label: 'Performing Provider', type: 'string' as const },
    { value: 'billingProviderName', label: 'Billing Provider', type: 'string' as const },
    { value: 'healthSystemCode', label: 'Health System Code', type: 'string' as const },
    { value: 'dateOfService', label: 'Date of Service', type: 'date' as const },
    { value: 'dateOfBirth', label: 'Date of Birth', type: 'date' as const },
];

const OPERATORS = [
    { value: '==', label: 'equals' },
    { value: '!=', label: 'not equals' },
];

const BOOLEAN_OPTIONS = [
    { value: 'true', label: 'true' },
    { value: 'false', label: 'false' },
];

// Helper: get fields for the selected entity
const getFieldsForEntity = (entity: string) =>
    entity === 'CODE' ? CODE_FIELDS : WORKITEM_FIELDS;

// Helper: get field type
const getFieldType = (entity: string, field: string) => {
    const fields = getFieldsForEntity(entity);
    return fields.find(f => f.value === field)?.type ?? 'string';
};

// Helper: format a value for SpEL expression
const formatValue = (value: string, fieldType: string): string => {
    if (fieldType === 'boolean') return value;
    if (fieldType === 'number') return value;
    if (fieldType === 'date') return `'${value}'`;
    return `'${value}'`;
};

// Helper: parse a simple SpEL expression into conditions
const parseExpression = (expression: string): { conditions: Condition[]; combinator: 'AND' | 'OR' } => {
    if (!expression) return { conditions: [{ entity: 'CODE', field: 'original', operator: '==', value: 'true' }], combinator: 'AND' };

    // Split by && or ||
    const isOr = expression.includes(' || ');
    const combinator: 'AND' | 'OR' = isOr ? 'OR' : 'AND';
    const parts = expression.split(isOr ? ' || ' : ' && ');

    const conditions: Condition[] = parts.map(part => {
        const trimmed = part.trim();
        // Match patterns like CODE.original == true or WORKITEM.facility == 'MainHospital'
        const match = trimmed.match(/^(CODE|WORKITEM)\.(\w+)\s*(==|!=|>|<|>=|<=)\s*(.+)$/);
        if (match) {
            let value = match[4].trim();
            // Remove surrounding quotes
            if (value.startsWith("'") && value.endsWith("'")) {
                value = value.slice(1, -1);
            }
            return { entity: match[1] as 'CODE' | 'WORKITEM', field: match[2], operator: match[3], value };
        }
        // Fallback: return a default condition
        return { entity: 'CODE' as const, field: 'original', operator: '==', value: 'true' };
    });

    return { conditions, combinator };
};

// Helper: build SpEL expression from conditions
const buildExpression = (conditions: Condition[], combinator: 'AND' | 'OR'): string => {
    return conditions
        .map(c => {
            const fieldType = getFieldType(c.entity, c.field);
            return `${c.entity}.${c.field} ${c.operator} ${formatValue(c.value, fieldType)}`;
        })
        .join(combinator === 'AND' ? ' && ' : ' || ');
};

const ExpressionBuilder: React.FC<ExpressionBuilderProps> = ({ expression, onChange, disabled = false }) => {
    const [conditions, setConditions] = useState<Condition[]>([]);
    const [combinator, setCombinator] = useState<'AND' | 'OR'>('AND');

    // Parse expression on mount or when expression changes externally
    useEffect(() => {
        const parsed = parseExpression(expression);
        setConditions(parsed.conditions);
        setCombinator(parsed.combinator);
    }, [expression]);

    const emitChange = useCallback((newConditions: Condition[], newCombinator: 'AND' | 'OR') => {
        const expr = buildExpression(newConditions, newCombinator);
        onChange(expr);
    }, [onChange]);

    const handleConditionChange = (index: number, field: keyof Condition, value: string) => {
        const updated = [...conditions];
        updated[index] = { ...updated[index], [field]: value };

        // Reset downstream fields when entity changes
        if (field === 'entity') {
            const fields = getFieldsForEntity(value);
            updated[index].field = fields[0]?.value ?? '';
            const fieldType = fields[0]?.type ?? 'string';
            updated[index].value = fieldType === 'boolean' ? 'true' : '';
        }

        // Reset value when field changes
        if (field === 'field') {
            const fieldType = getFieldType(updated[index].entity, value);
            updated[index].value = fieldType === 'boolean' ? 'true' : '';
        }

        setConditions(updated);
        emitChange(updated, combinator);
    };

    const handleAddCondition = () => {
        const newConditions = [...conditions, { entity: 'CODE' as const, field: 'original', operator: '==', value: 'true' }];
        setConditions(newConditions);
        emitChange(newConditions, combinator);
    };

    const handleRemoveCondition = (index: number) => {
        if (conditions.length <= 1) return;
        const newConditions = conditions.filter((_, i) => i !== index);
        setConditions(newConditions);
        emitChange(newConditions, combinator);
    };

    const handleCombinatorChange = (newCombinator: 'AND' | 'OR') => {
        setCombinator(newCombinator);
        emitChange(conditions, newCombinator);
    };

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">Expression</Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleAddCondition}
                    disabled={disabled}
                >
                    Add Condition
                </Button>
            </Stack>

            {conditions.map((condition, index) => {
                const fields = getFieldsForEntity(condition.entity);
                const fieldType = getFieldType(condition.entity, condition.field);

                return (
                    <React.Fragment key={index}>
                        {index > 0 && (
                            <FormControl size="small" sx={{ width: 100, alignSelf: 'center' }}>
                                <Select
                                    value={combinator}
                                    onChange={(e) => handleCombinatorChange(e.target.value as 'AND' | 'OR')}
                                    disabled={disabled}
                                >
                                    <MenuItem value="AND">AND</MenuItem>
                                    <MenuItem value="OR">OR</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                        <Box
                            sx={{
                                border: '1px solid #BBDCFB',
                                backgroundColor: '#F7FDFF',
                                borderRadius: '3px',
                                p: 2,
                            }}
                        >
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                alignItems={{ md: 'center' }}
                            >
                                <FormControl size="small" sx={{ minWidth: 130 }}>
                                    <InputLabel>Entity</InputLabel>
                                    <Select
                                        label="Entity"
                                        value={condition.entity}
                                        onChange={(e) => handleConditionChange(index, 'entity', e.target.value)}
                                        disabled={disabled}
                                    >
                                        <MenuItem value="CODE">CODE</MenuItem>
                                        <MenuItem value="WORKITEM">WORKITEM</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel>Field</InputLabel>
                                    <Select
                                        label="Field"
                                        value={condition.field}
                                        onChange={(e) => handleConditionChange(index, 'field', e.target.value)}
                                        disabled={disabled}
                                    >
                                        {fields.map(f => (
                                            <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        label="Operator"
                                        value={condition.operator}
                                        onChange={(e) => handleConditionChange(index, 'operator', e.target.value)}
                                        disabled={disabled}
                                    >
                                        {OPERATORS.map(op => (
                                            <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {fieldType === 'boolean' ? (
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                        <InputLabel>Value</InputLabel>
                                        <Select
                                            label="Value"
                                            value={condition.value}
                                            onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                                            disabled={disabled}
                                        >
                                            {BOOLEAN_OPTIONS.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : fieldType === 'date' ? (
                                    <TextField
                                        label="Value"
                                        type="date"
                                        value={condition.value}
                                        onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                                        size="small"
                                        sx={{ flex: 1, minWidth: 0 }}
                                        disabled={disabled}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                ) : (
                                    <TextField
                                        label="Value"
                                        value={condition.value}
                                        onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                                        size="small"
                                        sx={{ flex: 1, minWidth: 0 }}
                                        disabled={disabled}
                                    />
                                )}

                                {conditions.length > 1 && (
                                    <Tooltip title="Remove condition">
                                        <IconButton
                                            onClick={() => handleRemoveCondition(index)}
                                            disabled={disabled}
                                            size="small"
                                        >
                                            <DeleteOutline />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Box>
                    </React.Fragment>
                );
            })}

            <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: '3px' }}>
                <Typography variant="caption" color="text.secondary">
                    Generated expression:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                    {buildExpression(conditions, combinator) || '(empty)'}
                </Typography>
            </Box>
        </Stack>
    );
};

export default ExpressionBuilder;
